import { supabase } from '../lib/supabase';

export const paymentConfirmationService = {
  /**
   * Send payment confirmation email for any payment method
   * Supports: Stripe, BSP, Kinabank, Credit Bank
   */
  async sendPaymentConfirmation(paymentData) {
    try {
      const { 
        payment, 
        invoice, 
        company, 
        paymentMethod, 
        reconciliationStatus = 'pending' 
      } = paymentData;

      if (!payment || !invoice || !company || !paymentMethod) {
        throw new Error('Missing required payment confirmation data');
      }

      const { data: { session } } = await supabase?.auth?.getSession();
      
      const response = await fetch(
        `${supabase?.supabaseUrl}/functions/v1/send-payment-confirmation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            payment,
            invoice,
            company,
            paymentMethod,
            reconciliationStatus
          })
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Failed to send payment confirmation');
      }

      const result = await response?.json();

      // Queue notification in notification_queue
      await this.queuePaymentNotification(payment, invoice, company, paymentMethod, result);

      return result;
    } catch (error) {
      console.error('Error sending payment confirmation:', error);
      throw error;
    }
  },

  /**
   * Send Stripe payment confirmation
   */
  async sendStripePaymentConfirmation(paymentIntent, invoice, company) {
    try {
      const payment = {
        amount: paymentIntent?.amount / 100, // Convert from cents
        timestamp: new Date()?.toISOString(),
        transactionId: paymentIntent?.id,
        receiptNumber: `STRIPE-${paymentIntent?.id}`,
        id: paymentIntent?.id,
        paidAt: new Date()?.toISOString()
      };

      return await this.sendPaymentConfirmation({
        payment,
        invoice,
        company,
        paymentMethod: 'stripe',
        reconciliationStatus: 'reconciled' // Stripe payments are auto-reconciled
      });
    } catch (error) {
      console.error('Error sending Stripe payment confirmation:', error);
      throw error;
    }
  },

  /**
   * Send bank portal payment confirmation (BSP, Kinabank, Credit Bank)
   */
  async sendBankPaymentConfirmation(bankPaymentData, invoice, company, bankName) {
    try {
      const payment = {
        amount: invoice?.amountDue,
        timestamp: bankPaymentData?.timestamp || new Date()?.toISOString(),
        transactionId: bankPaymentData?.transactionId || null,
        receiptNumber: bankPaymentData?.receiptNumber || `${bankName?.toUpperCase()}-${Date.now()}`,
        id: bankPaymentData?.id || `bank-${Date.now()}`,
        paidAt: bankPaymentData?.timestamp || new Date()?.toISOString()
      };

      const paymentMethodMap = {
        'BSP': 'bsp',
        'Bank South Pacific': 'bsp',
        'Kinabank': 'kinabank',
        'Credit Bank': 'creditbank'
      };

      const paymentMethod = paymentMethodMap?.[bankName] || bankName?.toLowerCase();

      return await this.sendPaymentConfirmation({
        payment,
        invoice,
        company,
        paymentMethod,
        reconciliationStatus: 'pending' // Bank payments need manual reconciliation
      });
    } catch (error) {
      console.error('Error sending bank payment confirmation:', error);
      throw error;
    }
  },

  /**
   * Queue payment confirmation notification
   */
  async queuePaymentNotification(payment, invoice, company, paymentMethod, emailResult) {
    try {
      const { error } = await supabase
        ?.from('notification_queue')
        ?.insert({
          notification_type: 'payment_confirmation',
          priority: 'high',
          recipient_email: company?.contactEmail,
          subject: `Payment Confirmed: Invoice ${invoice?.invoiceNumber}`,
          message: `Payment confirmation sent for invoice ${invoice?.invoiceNumber} via ${paymentMethod}`,
          entity_type: 'payment_transaction',
          entity_id: payment?.id || payment?.transactionId,
          metadata: {
            invoice_number: invoice?.invoiceNumber,
            amount: payment?.amount,
            payment_method: paymentMethod,
            receipt_number: payment?.receiptNumber,
            reconciliation_status: emailResult?.reconciliationStatus,
            email_message_id: emailResult?.messageId
          },
          notification_status: 'sent',
          sent_at: new Date()?.toISOString()
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error queueing payment notification:', error);
    }
  },

  /**
   * Get payment receipt details
   */
  async getPaymentReceipt(paymentId, paymentMethod = 'stripe') {
    try {
      let query;
      
      if (paymentMethod === 'stripe') {
        query = supabase
          ?.from('payment_transactions')
          ?.select(`
            *,
            stripe_invoice:stripe_invoice_id (
              invoice_number,
              amount_due,
              billing_period_start,
              billing_period_end
            )
          `)
          ?.eq('id', paymentId)
          ?.single();
      } else {
        // For bank payments, query from appropriate table
        query = supabase
          ?.from('payment_transactions')
          ?.select('*')
          ?.eq('id', paymentId)
          ?.single();
      }

      const { data, error } = await query;
      if (error) throw error;

      return {
        id: data?.id,
        amount: data?.amount,
        receiptNumber: data?.receipt_number || data?.id,
        transactionId: data?.stripe_payment_intent_id || data?.transaction_reference,
        paymentMethod: paymentMethod,
        paidAt: data?.created_at,
        status: data?.status,
        invoice: data?.stripe_invoice
      };
    } catch (error) {
      console.error('Error fetching payment receipt:', error);
      throw error;
    }
  },

  /**
   * Update reconciliation status for bank payments
   */
  async updateReconciliationStatus(paymentId, status) {
    try {
      const validStatuses = ['pending', 'processing', 'reconciled', 'failed'];
      if (!validStatuses?.includes(status)) {
        throw new Error(`Invalid reconciliation status: ${status}`);
      }

      const { data, error } = await supabase
        ?.from('payment_transactions')
        ?.update({ 
          reconciliation_status: status,
          reconciled_at: status === 'reconciled' ? new Date()?.toISOString() : null,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', paymentId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating reconciliation status:', error);
      throw error;
    }
  },

  /**
   * Get all payments with reconciliation status
   */
  async getPaymentsWithReconciliation(companyId, filters = {}) {
    try {
      let query = supabase
        ?.from('payment_transactions')
        ?.select(`
          *,
          stripe_invoice:stripe_invoice_id (
            invoice_number,
            amount_due
          )
        `)
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });

      if (filters?.reconciliationStatus) {
        query = query?.eq('reconciliation_status', filters?.reconciliationStatus);
      }

      if (filters?.paymentMethod) {
        query = query?.eq('payment_method', filters?.paymentMethod);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(payment => ({
        id: payment?.id,
        amount: payment?.amount,
        paymentMethod: payment?.payment_method || 'stripe',
        status: payment?.status,
        reconciliationStatus: payment?.reconciliation_status || 'pending',
        receiptNumber: payment?.receipt_number || payment?.id,
        transactionId: payment?.stripe_payment_intent_id || payment?.transaction_reference,
        invoiceNumber: payment?.stripe_invoice?.invoice_number,
        paidAt: payment?.created_at,
        reconciledAt: payment?.reconciled_at
      })) || [];
    } catch (error) {
      console.error('Error fetching payments with reconciliation:', error);
      throw error;
    }
  }
};

export default paymentConfirmationService;